import React, { useEffect, useState } from 'react';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import { ICellRendererParams } from 'ag-grid-community';
import { AgileMacroplanogram, storeSubcategory } from '@components/agile/types/AgileMacroplanogramTypes';
import { AgileTableRow } from '@components/agile/types/AgileTableRow';
import Icon from '@amzn/meridian/icon';
import { ERROR_COLOR } from '@constants/values';
import infoKnockoutTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import Tooltip from '@amzn/meridian/tooltip';

const SubCategoriesCellRendererNew = (
  params: ICellRendererParams & {
    setRowData: (value: AgileTableRow[]) => void;
    rowData: AgileTableRow[];
    setRecommendationFlag: (value: boolean) => void;
    listOfMacroplanogram: AgileMacroplanogram[] | undefined;
    value: string;
    isNewAsinOnly?: boolean;
    onSubcategoryChange?: (asin: string, zoneName: string, newSubcategoryId: string) => void;
  },
) => {
  const { node, setRowData, rowData, listOfMacroplanogram, isNewAsinOnly, onSubcategoryChange } = params;
  const data = node.data as AgileTableRow;
  const [selectedCategory, setSelectedCategory] = useState<string>(data.subcategoryId as string);
  const [subCategoryList, setSubCategoryList] = useState<storeSubcategory[]>();
  const [errorFlag, setErrorFlag] = useState(!selectedCategory);

  const onClick = (subCategoryId: string) => {
    // Only proceed if the subcategory actually changed
    if (subCategoryId === selectedCategory) {
      return;
    }

    setSelectedCategory(subCategoryId);
    const tempData = [...rowData];

    // Find the current row by rowId for accurate identification
    const currentRowIndex = tempData.findIndex((item) => item.rowId === data.rowId);

    if (currentRowIndex !== -1) {
      // Check if there's another row with the same ASIN, zone, and new subcategory
      const existingRowIndex = tempData.findIndex(
        (item, index) =>
          index !== currentRowIndex &&
          item.asin === data.asin &&
          item.zoneName === data.zoneName &&
          item.subcategoryId === subCategoryId &&
          subCategoryId !== '',
      );

      if (existingRowIndex !== -1) {
        // Merge: add facings together and remove the current row
        const existingRow = tempData[existingRowIndex];
        const currentRow = tempData[currentRowIndex];

        const mergedRow = {
          ...existingRow,
          remainingFacings: existingRow.remainingFacings + currentRow.remainingFacings,
          difference: existingRow.remainingFacings + currentRow.remainingFacings - existingRow.availableFacings,
          remainingCapacity:
            (existingRow.fohCapacity.capacity || 0) * (existingRow.remainingFacings + currentRow.remainingFacings),
        };

        // Create new array with merged row and without the current row
        const newData = tempData
          .map((row, index) => (index === existingRowIndex ? mergedRow : row))
          .filter((_, index) => index !== currentRowIndex);

        setRowData(newData);
      } else {
        // No merge needed, just update the subcategory
        tempData[currentRowIndex].subcategoryId = subCategoryId;
        setRowData(tempData);
      }
    }
    setErrorFlag(false);

    // Call the subcategory change handler if provided
    if (onSubcategoryChange) {
      onSubcategoryChange(data.asin, data.zoneName, subCategoryId);
    }
  };

  useEffect(() => {
    const subCategories = !listOfMacroplanogram
      ? []
      : (listOfMacroplanogram.find((macroplanogram) => macroplanogram.entryId === data.macroplanogramId)
          ?.storeSubcategories ?? []);
    setSubCategoryList(subCategories);
  }, [listOfMacroplanogram, data.macroplanogramId]);

  // Show dropdown for new ASINs or ADD actions if isNewAsinOnly is true, otherwise show for all
  if (isNewAsinOnly && !data.isNewAsin && data.recommendedAction !== 'ADD') {
    const subcategoryName =
      subCategoryList?.find((subcat) => subcat.subcategoryId === selectedCategory)?.subcategoryName ||
      selectedCategory ||
      'N/A';
    return <span>{subcategoryName}</span>;
  }

  return (
    <Row spacing="none" spacingInset="none" maxWidth="50">
      <Select
        disabled={!subCategoryList || subCategoryList.length <= 0}
        value={selectedCategory}
        onChange={onClick}
        width={130}
        error={errorFlag}
      >
        {!subCategoryList ||
          (subCategoryList.length <= 0 && <SelectOption value={''} label={'Select Zone'} key={'SelectZone'} />)}
        {subCategoryList?.map((subCategory) => (
          <SelectOption
            value={subCategory.subcategoryId}
            label={subCategory.subcategoryName}
            key={subCategory.subcategoryId}
          />
        ))}
      </Select>
      {errorFlag ? (
        <Tooltip position="end" title="Please select a subcategory" id="subcategoryTooltip">
          <Icon className={ERROR_COLOR} tokens={infoKnockoutTokens} />
        </Tooltip>
      ) : null}
    </Row>
  );
};

export default SubCategoriesCellRendererNew;
